import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useStyles } from './FlyoutCasProgramSetup.styles'
import {
  ButtonV2,
  CFSButtonV2,
  ExternalLink,
  NoteV2,
  TextInputV4,
} from '@labrav/react-components'
import {
  Sections,
  TestConnectionStatusProps,
} from '../../../modules/flyOutOnboarding/reducer'
import { FormikProps, useFormikContext } from 'formik'
import { FormLockedType } from '../../../types/FormLockedType'
import {
  flyOutTestConnection,
  flyOutTestConnectionRoleAndDatafeeds,
} from '../../../modules/flyOutOnboarding/action'
import { useFlagValue } from '@labrav/flags'
import { ShowConnectionStatuses } from '../FlyOutArnDetails/FlyOutArnDetails'
import {
  emptySpaceCharsAtStartAndEndRegex,
  flyOutDataFeedsCFTLink,
  flyoutFormDebounceTime,
  newDataFeedProgramLink,
} from '../../../common/utils/constants'
import { useAccountId } from '@labrav/common'
import { useDispatch } from '../../../store'
import { Typography } from '@mui/material'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faCircleCheck,
  faCircleXmark,
  faCircleDashed,
} from '@fortawesome/pro-regular-svg-icons'
import { faLoader } from '@fortawesome/pro-duotone-svg-icons'
import {
  TERRAFORM_VALIDATION_STEPS,
  type TerraformValidationStepStatus,
} from './constants'

export type FlyoutCasProgramSetupComponentProps = FormLockedType & {
  showCloudFormationDataFeedsSetup?: boolean
  enableDataFeedsRevampBehavior?: boolean
}

export const FlyoutCasProgramSetup: React.FC<
  FlyoutCasProgramSetupComponentProps
> = ({
  isFormLocked,
  showCloudFormationDataFeedsSetup = true,
  enableDataFeedsRevampBehavior = false,
}) => {
  const { classes, cx } = useStyles({ showCloudFormationDataFeedsSetup })
  const dispatch = useDispatch()
  const accountId = useAccountId({ product: 'flyout' })
  const {
    values,
    errors,
    touched,
    status,
    setStatus,
    handleBlur,
    handleChange,
    setFieldValue,
    setFieldTouched,
    setFieldError,
    validateForm,
  } = useFormikContext() as FormikProps<Sections>
  const testConnectionDebounceRef = useRef<ReturnType<
    typeof setTimeout
  > | null>(null)
  const [casTestConnectionStatus, setCasTestConnectionStatus] =
    useState<TestConnectionStatusProps>({
      status: 'standBy',
    })
  const previousCloudRoleArnRef = useRef(
    values.cloudSettings.cloudRoleArn || ''
  )
  const previousCloudCasS3BucketNameRef = useRef(
    values.cloudSettings.cloudCasS3BucketName || ''
  )

  const [terraformValidationSteps, setTerraformValidationSteps] = useState(
    TERRAFORM_VALIDATION_STEPS
  )
  const [terraformValidationPanelVisible, setTerraformValidationPanelVisible] =
    useState(false)
  const { value: isMarketplaceCloudSettingsRevampEnabled } = useFlagValue(
    'marketplaceCloudSettingsRevamp'
  )
  const { value: isOnboardingImprovementsEnabled } = useFlagValue(
    'onboardingImprovements'
  )
  const isTerraformStack =
    Boolean(isOnboardingImprovementsEnabled) &&
    values.cloudSettings.stackType === 'terraform'
  const cloudSettings = values.cloudSettings
  const settingsTouched = touched?.cloudSettings
  const settingsError = errors?.cloudSettings
  const s3BucketArnFilled = !!cloudSettings.cloudCasS3BucketName
  const showCloudCasS3BucketNameError =
    !!(settingsTouched?.cloudCasS3BucketName || s3BucketArnFilled) &&
    settingsError?.cloudCasS3BucketName
  const testConnectionStatus = values.cloudSettings.testConnectionStatus
  const isS3BlockedByArnFailure =
    enableDataFeedsRevampBehavior &&
    showCloudFormationDataFeedsSetup &&
    values.cloudSettings.testConnectionStatus === 'failure'
  const failedConnectionField = (
    status as {
      failedConnectionField?: 'cloudRoleArn' | 'cloudCasS3BucketName'
    }
  )?.failedConnectionField
  const isS3ConnectionFailure = showCloudFormationDataFeedsSetup
    ? enableDataFeedsRevampBehavior &&
      casTestConnectionStatus.status === 'failure'
    : testConnectionStatus === 'failure' &&
      failedConnectionField === 'cloudCasS3BucketName'
  const applyDataFeedsConnectionTestResult = (
    connectionStatus: TestConnectionStatusProps,
    failureMessage?: string
  ) => {
    if (
      !enableDataFeedsRevampBehavior ||
      values.cloudSettings.isOwnS3Bucket !== true
    ) {
      return
    }
    const isSuccess = connectionStatus.status === 'success'
    void setFieldValue('cloudSettings.dataFeedsConnectionTested', isSuccess)
    setStatus?.({
      ...(status as Record<string, unknown>),
      dataFeedsConnectionStatus: connectionStatus.status,
    })
    if (isSuccess) {
      void setFieldError('cloudSettings.dataFeedsConnectionTested', undefined)
      return
    }
    if (connectionStatus.status === 'failure') {
      const message = failureMessage
      void setFieldError('cloudSettings.dataFeedsConnectionTested', message)
      void setFieldTouched(
        'cloudSettings.dataFeedsConnectionTested',
        true,
        false
      )
    }
  }

  useEffect(() => {
    if (!isTerraformStack) {
      return
    }
    if (values.cloudSettings.terraformConnectionValidated !== undefined) {
      return
    }
    void setFieldValue('cloudSettings.terraformConnectionValidated', false)
  }, [
    isTerraformStack,
    values.cloudSettings.terraformConnectionValidated,
    setFieldValue,
  ])

  useEffect(() => {
    if (!isTerraformStack) {
      setTerraformValidationPanelVisible(false)
      return
    }
    if (values.cloudSettings.terraformConnectionValidated === true) {
      setTerraformValidationPanelVisible(true)
      setTerraformValidationSteps(
        TERRAFORM_VALIDATION_STEPS.map(step => ({
          ...step,
          status: 'success' as TerraformValidationStepStatus,
        }))
      )
      return
    }
    setTerraformValidationPanelVisible(false)
    setTerraformValidationSteps(TERRAFORM_VALIDATION_STEPS)
    setLoading(false)
  }, [isTerraformStack, values.cloudSettings.terraformConnectionValidated])

  useEffect(() => {
    if (!enableDataFeedsRevampBehavior) {
      return
    }
    if (showCloudFormationDataFeedsSetup) {
      return
    }
    setCasTestConnectionStatus({ status: 'standBy' })
    setStatus?.({
      ...(status as Record<string, unknown>),
      failedConnectionField: undefined,
    })
    void updateFlyoutTestConnection('standBy')
    if (testConnectionDebounceRef.current) {
      clearTimeout(testConnectionDebounceRef.current)
    }
    const cloudRoleArn = values.cloudSettings.cloudRoleArn || ''
    const cloudCasS3BucketName = values.cloudSettings.cloudCasS3BucketName || ''

    if (cloudCasS3BucketName && !cloudRoleArn) {
      void setFieldTouched('cloudSettings.cloudRoleArn', true, true)
    }

    if (!cloudRoleArn || !cloudCasS3BucketName) {
      return
    }

    if (!emptySpaceCharsAtStartAndEndRegex.test(cloudCasS3BucketName)) {
      return
    }

    if (errors?.cloudSettings?.cloudCasS3BucketName) {
      return
    }

    const changedField: 'cloudRoleArn' | 'cloudCasS3BucketName' =
      previousCloudRoleArnRef.current !== cloudRoleArn
        ? 'cloudRoleArn'
        : 'cloudCasS3BucketName'

    testConnectionDebounceRef.current = setTimeout(() => {
      void dispatch(
        flyOutTestConnectionRoleAndDatafeeds(
          accountId || '',
          cloudRoleArn,
          cloudCasS3BucketName,
          async connectionStatus => {
            setCasTestConnectionStatus(connectionStatus)
            setStatus?.({
              ...(status as Record<string, unknown>),
              combinedConnectionStatus: connectionStatus.status,
              failedConnectionField:
                connectionStatus.status !== 'success'
                  ? changedField
                  : undefined,
            })
            await setFieldValue(
              'cloudSettings.testConnectionStatus',
              connectionStatus.status
            )
          }
        )
      )
    }, flyoutFormDebounceTime)
    previousCloudRoleArnRef.current = cloudRoleArn
    previousCloudCasS3BucketNameRef.current = cloudCasS3BucketName
  }, [
    showCloudFormationDataFeedsSetup,
    values.cloudSettings.cloudRoleArn,
    values.cloudSettings.cloudCasS3BucketName,
    errors?.cloudSettings?.cloudCasS3BucketName,
  ])

  useEffect(() => {
    if (!enableDataFeedsRevampBehavior) {
      return
    }
    if (!showCloudFormationDataFeedsSetup) {
      return
    }
    setCasTestConnectionStatus({ status: 'standBy' })
    setStatus?.({
      ...(status as Record<string, unknown>),
      failedConnectionField: undefined,
    })
    if (testConnectionDebounceRef.current) {
      clearTimeout(testConnectionDebounceRef.current)
    }
    const cloudCasS3BucketName = values.cloudSettings.cloudCasS3BucketName || ''
    const cloudRoleArn = values.cloudSettings.cloudRoleArn || ''
    if (!cloudCasS3BucketName) {
      return
    }
    if (!emptySpaceCharsAtStartAndEndRegex.test(cloudCasS3BucketName)) {
      return
    }
    if (values.cloudSettings.testConnectionStatus === 'failure') {
      const arnBlockedMessage =
        'S3 bucket connection failed because ARN connection failed.'
      setCasTestConnectionStatus({
        status: 'failure',
        error: arnBlockedMessage,
      })
      applyDataFeedsConnectionTestResult(
        { status: 'failure', error: arnBlockedMessage },
        arnBlockedMessage
      )
      return
    }
    if (cloudCasS3BucketName && !cloudRoleArn) {
      void setFieldTouched('cloudSettings.cloudRoleArn', true, true)
    }
    if (!cloudRoleArn) {
      return
    }
    testConnectionDebounceRef.current = setTimeout(() => {
      void dispatch(
        flyOutTestConnectionRoleAndDatafeeds(
          accountId || '',
          cloudRoleArn,
          cloudCasS3BucketName,
          connectionStatus => {
            setCasTestConnectionStatus(connectionStatus)
            applyDataFeedsConnectionTestResult(connectionStatus)
          }
        )
      )
    }, flyoutFormDebounceTime)
  }, [
    showCloudFormationDataFeedsSetup,
    values.cloudSettings.testConnectionStatus,
    values.cloudSettings.cloudRoleArn,
    values.cloudSettings.cloudCasS3BucketName,
    errors?.cloudSettings?.cloudCasS3BucketName,
  ])

  useEffect(() => {
    return () => {
      if (testConnectionDebounceRef.current) {
        clearTimeout(testConnectionDebounceRef.current)
      }
    }
  }, [])

  const updateFlyoutTestConnection = async (status: 'standBy') => {
    await setFieldValue('cloudSettings.testConnectionStatus', status)
  }
  const [loading, setLoading] = useState(false)

  const updateTerraformValidationStep = (
    stepIndex: number,
    status: TerraformValidationStepStatus,
    steps: typeof TERRAFORM_VALIDATION_STEPS
  ) =>
    steps.map((step, index) =>
      index === stepIndex ? { ...step, status } : step
    )

  const syncTerraformConnectionValidated = async (validated: boolean) => {
    await setFieldValue('cloudSettings.terraformConnectionValidated', validated)
    if (validated) {
      void setFieldError(
        'cloudSettings.terraformConnectionValidated',
        undefined
      )
    }
  }

  const resetTerraformConnectionValidation = () => {
    void setFieldValue('cloudSettings.terraformConnectionValidated', false)
    void setFieldError('cloudSettings.terraformConnectionValidated', undefined)
    void setFieldTouched(
      'cloudSettings.terraformConnectionValidated',
      false,
      false
    )
    setTerraformValidationSteps(TERRAFORM_VALIDATION_STEPS)
    setTerraformValidationPanelVisible(false)
  }

  const handleValidateTerraformConnection = async () => {
    await setFieldTouched('cloudSettings.cloudRoleArn', true, true)
    if (values.cloudSettings.cloudCasS3BucketName) {
      await setFieldTouched('cloudSettings.cloudCasS3BucketName', true, true)
    }

    const formErrors = (await validateForm()) as {
      cloudSettings?: {
        cloudRoleArn?: string
        cloudCasS3BucketName?: string
      }
    }

    if (
      formErrors.cloudSettings?.cloudRoleArn ||
      formErrors.cloudSettings?.cloudCasS3BucketName
    ) {
      await syncTerraformConnectionValidated(false)
      return
    }

    const cloudRoleArn = values.cloudSettings.cloudRoleArn
    const cloudCasS3BucketName = values.cloudSettings.cloudCasS3BucketName

    setTerraformValidationPanelVisible(true)
    setLoading(true)
    let steps = TERRAFORM_VALIDATION_STEPS.map(step => ({
      ...step,
      status: 'pending' as TerraformValidationStepStatus,
    }))
    setTerraformValidationSteps(steps)
    await syncTerraformConnectionValidated(false)

    const runDataFeedsValidation = () =>
      new Promise<void>(resolve => {
        if (!cloudCasS3BucketName) {
          resolve()
          return
        }

        void dispatch(
          flyOutTestConnectionRoleAndDatafeeds(
            accountId || '',
            cloudRoleArn,
            cloudCasS3BucketName,
            connectionStatus => {
              if (
                connectionStatus.status !== 'success' &&
                connectionStatus.status !== 'failure'
              ) {
                return
              }
              const s3Status: TerraformValidationStepStatus =
                connectionStatus.status === 'success' ? 'success' : 'failed'
              steps = updateTerraformValidationStep(1, s3Status, steps)
              steps = updateTerraformValidationStep(
                2,
                connectionStatus.status === 'success' ? 'success' : 'pending',
                steps
              )
              setTerraformValidationSteps(steps)
              resolve()
            }
          )
        )
      })

    try {
      await new Promise<void>(resolve => {
        void dispatch(
          flyOutTestConnection(
            accountId || '',
            cloudRoleArn,
            isMarketplaceCloudSettingsRevampEnabled,
            async connectionStatus => {
              if (
                connectionStatus.status !== 'success' &&
                connectionStatus.status !== 'failure'
              ) {
                return
              }

              const arnStatus: TerraformValidationStepStatus =
                connectionStatus.status === 'success' ? 'success' : 'failed'
              steps = updateTerraformValidationStep(0, arnStatus, steps)
              setTerraformValidationSteps(steps)

              if (connectionStatus.status === 'failure') {
                steps = updateTerraformValidationStep(1, 'failed', steps)
                steps = updateTerraformValidationStep(2, 'pending', steps)
                setTerraformValidationSteps(steps)
                resolve()
                return
              }

              await runDataFeedsValidation()
              resolve()
            }
          )
        )
      })
      const allStepsValidated = steps.every(step => step.status === 'success')
      await syncTerraformConnectionValidated(allStepsValidated)
    } finally {
      setLoading(false)
    }
  }

  const terraformValidatedCount = terraformValidationSteps.filter(
    step => step.status === 'success'
  ).length

  const handleChangeField = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    await handleChange(event)
    if (isTerraformStack) {
      resetTerraformConnectionValidation()
    }
    if (event.target.name === 'cloudSettings.cloudCasS3BucketName') {
      void setFieldValue('cloudSettings.dataFeedsConnectionTested', false)
      void setFieldError('cloudSettings.dataFeedsConnectionTested', undefined)
      setCasTestConnectionStatus({ status: 'standBy' })
    }
    setFieldValue('cloudSettings.formFilled', false)
    setFieldValue('cloudSettings.formUpdated', true)
  }

  const cftLaunchLink = useMemo(() => {
    const baseUrl = flyOutDataFeedsCFTLink
    const s3BucketArn = String(values.cloudSettings.cloudCasS3BucketName)
    const arnRole = String(values.cloudSettings.cloudRoleArn)
    const kmsKeyArn = String(values.cloudSettings.cloudKmsKeyArn)
    const snsTopic = String(values.cloudSettings.cloudCasSnsTopic)

    return `${baseUrl}&param_KmsKeyArn=${encodeURIComponent(kmsKeyArn)}&param_LabraPlatformRole=${encodeURIComponent(arnRole)}&param_S3BucketArn=${encodeURIComponent(s3BucketArn)}&param_SnsTopicArn=${encodeURIComponent(snsTopic)}`
  }, [
    values.cloudSettings.cloudRoleArn,
    values.cloudSettings.cloudKmsKeyArn,
    values.cloudSettings.cloudCasSnsTopic,
    values.cloudSettings.cloudCasS3BucketName,
  ])

  const showTerraformValidationError =
    isTerraformStack &&
    !!settingsTouched?.terraformConnectionValidated &&
    !!settingsError?.terraformConnectionValidated

  return (
    <div className={classes.fieldsDataFeedsContainer}>
      {!enableDataFeedsRevampBehavior ? (
        <CFSButtonV2 cfLink={flyOutDataFeedsCFTLink} />
      ) : null}

      {showCloudFormationDataFeedsSetup ? (
        <>
          <div
            className={classes.subHeadingContainer}
            data-testid="data-feeds-text-and-link"
          >
            <NoteV2
              description={
                <>
                  Go to{' '}
                  <ExternalLink href={newDataFeedProgramLink}>
                    this link
                  </ExternalLink>{' '}
                  , copy the following values: S3 bucket ARN, KMS key ARN, SNS
                  topic ARN and paste each value into corresponding fields
                  below.
                </>
              }
            />
          </div>
        </>
      ) : null}
      <div className={classes.inputsColumn}>
        <div className={cx(classes.field, classes.fieldTopAligned)}>
          <TextInputV4
            keyName="cloudSettings.cloudCasS3BucketName"
            title="S3 bucket ARN"
            type="text"
            placeHolder=""
            value={cloudSettings.cloudCasS3BucketName}
            isError={!!showCloudCasS3BucketNameError || isS3ConnectionFailure}
            onChange={handleChangeField}
            onBlur={handleBlur}
            isReadOnly={isFormLocked as boolean}
            isRequired={false}
            errorText={showCloudCasS3BucketNameError || ''}
          />
        </div>

        <div className={classes.field} data-testid="aws-kms-key-arn-input">
          <TextInputV4
            keyName="cloudSettings.cloudKmsKeyArn"
            title="KMS key ARN"
            type="string"
            placeHolder=""
            value={cloudSettings.cloudKmsKeyArn}
            isError={
              !!(
                settingsTouched?.cloudKmsKeyArn && settingsError?.cloudKmsKeyArn
              )
            }
            onChange={handleChangeField}
            isReadOnly={isFormLocked as boolean}
            isRequired={false}
            errorText={
              (!!settingsTouched?.cloudKmsKeyArn &&
                settingsError?.cloudKmsKeyArn) ||
              ''
            }
            showSpinner={true}
            onBlur={handleBlur}
          />
        </div>
        <div className={classes.field}>
          <TextInputV4
            keyName="cloudSettings.cloudCasSnsTopic"
            title="SNS topic ARN"
            type="string"
            placeHolder=""
            value={cloudSettings.cloudCasSnsTopic}
            isError={
              !!(
                settingsTouched?.cloudCasSnsTopic &&
                settingsError?.cloudCasSnsTopic
              )
            }
            onChange={handleChangeField}
            isReadOnly={isFormLocked as boolean}
            isRequired={false}
            errorText={
              (!!settingsTouched?.cloudCasSnsTopic &&
                settingsError?.cloudCasSnsTopic) ||
              ''
            }
            showSpinner={false}
            onBlur={handleBlur}
          />
        </div>
        {enableDataFeedsRevampBehavior && !isTerraformStack ? (
          <div className={classes.dataFeedsRevampFooter}>
            {showCloudFormationDataFeedsSetup ? (
              <>
                <Typography className={classes.sectionText}>
                  Once all three fields are filled, click “Launch CloudFormation
                  Stack” to complete the setup.
                </Typography>
                <CFSButtonV2 cfLink={cftLaunchLink} />
              </>
            ) : null}

            <div data-testid="test-connection-s3">
              <ShowConnectionStatuses
                testConnectionStatus={
                  showCloudFormationDataFeedsSetup
                    ? casTestConnectionStatus
                    : { status: testConnectionStatus }
                }
                successMessage={
                  showCloudFormationDataFeedsSetup
                    ? 'Labra has successfully connected to your S3 bucket.'
                    : undefined
                }
                failureMessage={
                  showCloudFormationDataFeedsSetup
                    ? isS3BlockedByArnFailure
                      ? 'S3 bucket connection failed because ARN connection failed.'
                      : 'S3 bucket connection failed.'
                    : undefined
                }
              />
            </div>
          </div>
        ) : null}
        {isTerraformStack ? (
          <>
            <ButtonV2
              data-testid="validate-connection-button"
              styleType="outlined"
              className={cx(
                classes.validateConnectionButton,
                loading && classes.validateConnectionButtonLoading
              )}
              onClick={() => void handleValidateTerraformConnection()}
              disabled={loading}
            >
              <span className={classes.validateConnectionButtonContent}>
                <span className={classes.validateConnectionButtonLabel}>
                  {loading ? 'Validating connection' : 'Validate connection'}
                </span>
                {loading ? (
                  <FontAwesomeIcon
                    icon={faLoader}
                    spin
                    className={classes.validateConnectionButtonLoader}
                    data-testid="validate-connection-button-loader"
                  />
                ) : null}
              </span>
            </ButtonV2>
            {showTerraformValidationError ? (
              <Typography
                id="cloudSettings.terraformConnectionValidated"
                className={classes.terraformValidationRequiredError}
                data-testid="terraform-connection-validation-error"
              >
                {String(settingsError?.terraformConnectionValidated)}
              </Typography>
            ) : null}
            {terraformValidationPanelVisible ? (
              <div
                className={classes.terraformValidationPanel}
                data-testid="terraform-validation-panel"
              >
                <div className={classes.terraformValidationHeader}>
                  <div>Validating setup</div>
                  <div>{terraformValidatedCount}/3 Validated</div>
                </div>
                <div className={classes.terraformValidationStepsList}>
                  {terraformValidationSteps.map(step => (
                    <div
                      key={step.label}
                      className={classes.terraformValidationStepItem}
                    >
                      <div className={classes.terraformValidationStepRow}>
                        {step.status === 'success' && (
                          <FontAwesomeIcon
                            icon={faCircleCheck}
                            className={classes.terraformStepIconSuccess}
                          />
                        )}
                        {step.status === 'failed' && (
                          <FontAwesomeIcon
                            icon={faCircleXmark}
                            className={classes.terraformStepIconFailed}
                          />
                        )}
                        {step.status === 'pending' && (
                          <FontAwesomeIcon
                            icon={faCircleDashed}
                            className={classes.terraformStepIconPending}
                          />
                        )}
                        {step.label}
                      </div>
                      {step.status === 'failed' && (
                        <div className={classes.terraformValidationErrorBox}>
                          <div className={classes.terraformValidationErrorText}>
                            {`${step.errorTitle}\n${step.errorMessage}`}
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ) : null}
          </>
        ) : null}
        {!showCloudFormationDataFeedsSetup ? (
          <div
            className={classes.subHeadingContainer}
            data-testid="data-feeds-text-and-link"
          >
            <NoteV2
              description={
                <>
                  Go to{' '}
                  <ExternalLink href={newDataFeedProgramLink}>
                    this link
                  </ExternalLink>{' '}
                  , and paste the following values into their corresponding
                  fields in AMMP: S3 Bucket ARN, KMS Key ARN, and SNS Topic ARN.
                </>
              }
            />
          </div>
        ) : null}
      </div>
    </div>
  )
}
